import { api } from '../../../shared/lib/axios';
import { downloadAuthenticatedFile, openAuthenticatedFile } from '../../../shared/lib/files';

/** Documentos do colaborador (change `pessoas-documentos-contatos-bancos`). */

export type DocumentStatus =
  | 'aguardando_envio' | 'recebido' | 'em_validacao' | 'validado' | 'validado_com_ressalva' | 'rejeitado'
  | 'correcao_solicitada' | 'vencimento_proximo' | 'vencido' | 'substituido' | 'cancelado';

export type Confidentiality = 'interno' | 'restrito' | 'bancario' | 'medico';

export interface DocumentTypeItem {
  id: string;
  code: string;
  name: string;
  category: string;
  defaultConfidentiality: Confidentiality;
  hasExpiry: boolean;
  defaultValidityMonths: number | null;
  expiryAlertDays: number;
  allowedKinds: string[];
  maxSizeMb: number;
  requiredFor: string[];
  blocksAdmissionFor: string[];
  visibleToCollaborator: boolean;
  collaboratorCanUpload: boolean;
  segregatedValidation: boolean;
  active: boolean;
}

export type DocumentTypePayload = Omit<DocumentTypeItem, 'id' | 'active'>;

export interface CollaboratorDocument {
  id: string;
  userId: string;
  documentTypeId: string | null;
  typeCode: string;
  typeName: string;
  category: string | null;
  employmentBondId: string | null;
  status: DocumentStatus;
  statusLabel: string;
  version: number;
  previousVersionId: string | null;
  current: boolean;
  valid: boolean;
  title: string | null;
  documentNumber: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  confidentiality: Confidentiality;
  origin: string;
  fileName: string;
  validationNote: string | null;
  rejectionReason: string | null;
  rejectionReasonLabel: string | null;
  rejectionComment: string | null;
  cancelledReason: string | null;
  uploadedAt: string;
}

export interface ChecklistItem {
  type: {
    id: string; code: string; name: string; category: string; confidentiality: Confidentiality;
    visibleToCollaborator: boolean; collaboratorCanUpload: boolean; allowedKinds: string[]; maxSizeMb: number;
  };
  blocksAdmission: boolean;
  satisfied: boolean;
  document: { id: string; status: DocumentStatus; version: number; expiresAt: string | null } | null;
}

export interface UploadPayload {
  documentTypeId: string;
  employmentBondId?: string;
  previousVersionId?: string;
  title?: string;
  documentNumber?: string;
  issuedAt?: string;
  expiresAt?: string;
  origin?: 'manual' | 'email' | 'digitalizacao' | 'contabilidade';
  allowDuplicate?: boolean;
  file: File;
}

export type ReviewAction = 'validate' | 'validate_with_note' | 'reject' | 'request_correction';

function list<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  const data = (body as { data?: unknown } | null)?.data;
  return Array.isArray(data) ? (data as T[]) : [];
}

function object<T>(body: unknown): T {
  const { version: _v, _links: _l, ...rest } = (body ?? {}) as Record<string, unknown>;
  return rest as T;
}

function form(payload: UploadPayload): FormData {
  const data = new FormData();
  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined || value === null || value === '' || key === 'file') continue;
    data.append(key, String(value));
  }
  data.append('file', payload.file);
  return data;
}

// --- Tipos ---------------------------------------------------------

export async function listarTiposDocumentais(active: 'true' | 'false' | 'all' = 'true'): Promise<DocumentTypeItem[]> {
  const { data } = await api.get('/document-types', { params: { active } });
  return list<DocumentTypeItem>(data);
}

export async function salvarTipoDocumental(id: string | null, payload: Partial<DocumentTypePayload>): Promise<DocumentTypeItem> {
  const { data } = id ? await api.patch(`/document-types/${id}`, payload) : await api.post('/document-types', payload);
  return object<DocumentTypeItem>(data);
}

export async function situacaoTipoDocumental(id: string, active: boolean): Promise<DocumentTypeItem> {
  const { data } = await api.patch(`/document-types/${id}/active`, { active });
  return object<DocumentTypeItem>(data);
}

// --- Documentos (DP) -----------------------------------------------

export async function listarDocumentos(personId: string, history = false): Promise<CollaboratorDocument[]> {
  const { data } = await api.get(`/collaborators/${personId}/documents`, { params: { history: String(history) } });
  return list<CollaboratorDocument>(data);
}

export async function checklistDoVinculo(bondId: string): Promise<ChecklistItem[]> {
  const { data } = await api.get(`/employment-bonds/${bondId}/document-checklist`);
  return list<ChecklistItem>(data);
}

export async function enviarDocumento(personId: string, payload: UploadPayload): Promise<CollaboratorDocument> {
  const { data } = await api.post(`/collaborators/${personId}/documents`, form(payload));
  return object<CollaboratorDocument>(data);
}

export async function versoesDocumento(docId: string): Promise<CollaboratorDocument[]> {
  const { data } = await api.get(`/collaborator-documents/${docId}/versions`);
  return list<CollaboratorDocument>(data);
}

export async function analisarDocumento(
  docId: string, action: ReviewAction, reason?: string, comment?: string,
): Promise<CollaboratorDocument> {
  const { data } = await api.post(`/collaborator-documents/${docId}/review`, { action, reason, comment });
  return object<CollaboratorDocument>(data);
}

export async function cancelarDocumento(docId: string, justification: string): Promise<CollaboratorDocument> {
  const { data } = await api.post(`/collaborator-documents/${docId}/cancel`, { justification });
  return object<CollaboratorDocument>(data);
}

export function visualizarDocumento(docId: string) {
  return openAuthenticatedFile(`/api/collaborator-documents/${docId}/view`);
}

export function baixarDocumento(doc: CollaboratorDocument, purpose?: string) {
  const qs = purpose ? `?purpose=${encodeURIComponent(purpose)}` : '';
  return downloadAuthenticatedFile(`/api/collaborator-documents/${doc.id}/download${qs}`, doc.fileName);
}

// --- Portal --------------------------------------------------------

export async function meusDocumentos(): Promise<CollaboratorDocument[]> {
  const { data } = await api.get('/me/documents');
  return list<CollaboratorDocument>(data);
}

export async function meuChecklist(): Promise<ChecklistItem[]> {
  const { data } = await api.get('/me/documents/checklist');
  return list<ChecklistItem>(data);
}

export async function meusTiposDocumentais(): Promise<DocumentTypeItem[]> {
  const { data } = await api.get('/me/document-types');
  return list<DocumentTypeItem>(data);
}

export async function enviarMeuDocumento(payload: UploadPayload): Promise<CollaboratorDocument> {
  const { data } = await api.post('/me/documents', form(payload));
  return object<CollaboratorDocument>(data);
}

export function visualizarMeuDocumento(docId: string) {
  return openAuthenticatedFile(`/api/me/documents/${docId}/view`);
}
